import * as fsFn from 'fs';
import Response from './response';
import {pathMatch} from './util.path-match';

import { Config } from './index';
import { ExtendedObject } from './info';

async function stat(path: string): Promise<fsFn.Stats> {
	return new Promise((a, b) => fsFn.stat(path, (e, d) => e ? b(e) : a(d)));
}

export interface staticActive {
	(module: string, path: string, appId: string, vpath: string, ): Promise<Response | undefined>;
}

export interface StaticActive {
	(staticActive: staticActive, module: string, path: string, appId: string, vpath: string): Response | undefined | Promise<Response | undefined>
}
/**
 * 获取真实路径
 * @param  {String} p 转化后路径
 * @return {Array}   真实路径信息
 */
async function getStaticResponse(
	this: {
		basePath?(appId: string): string | string[];
		index: string[];
		extension: string[];
	},
	module: string,
	path: string,
	appId: string,
	vpath: string,
): Promise<Response | undefined> {
	const {basePath = (appId: string) => [appId ? `app/${appId}/public/` : `public/`], index, extension} = this;
	let appPath = basePath(appId);
	if (!Array.isArray(appPath)) { appPath = [appPath]; }
	const realPath = module + path;

	//如果为目录，则返回索引文件
	if (realPath[realPath.length - 1] === '/') {
		for (let p of appPath) {
			for (let indexName of index) {
				try {
					if ((await stat(p + realPath + indexName)).isFile()) {
						return new Response({ file: realPath + indexName, appId, appPath: p});
					}
				} catch(e) {}
			}
		}
		return ;
	}

	//如果文件存在,且为文件，则返回
	let isDir: boolean = false;
	for (let p of appPath) {
		try {
			let info = await stat(p + realPath);
			if (info.isFile()) {
				return new Response({file: realPath, appId, appPath: p});
			}
			isDir = isDir || info.isDirectory();
		} catch(e) {}
	}
	//如果文件.html存在，且为文件，则返回
	for (let p of appPath) {
		for (let ext of extension) {
			try {
				if ((await stat(p + realPath + ext)).isFile()) {
					return new Response({file: realPath + ext, appId, appPath: p});
				}
			} catch(e) {}
		}
	}
	if (isDir) { return new Response({transfer: `${vpath}${path}/`, appId}); }
}

interface serverThis<Ext extends ExtendedObject = {user: object}> {
	config: Config<Ext>,
	match: pathMatch;
	basePath(appId: string): string | string[];
	index(accept: string[]): string | string[];
	extension(accept: string[]): string | string[];
	active?: StaticActive;
}
export interface Opt {
	/** 获取请求域名 */
	readonly domain: string;
	/** 获取请求方法 */
	readonly method: string;
	/** 获取请求路径 */
	readonly path: string;
	/** 客户端IP */
	readonly clientIp: string[];
	getHeader(h: string): undefined | string | string[];
	setHeader(h: string, v: string | string[]): void;
}
export interface server {
	(this: serverThis | void, staticPath: string | string[], opt: Opt): Promise<Response>;
}
/**
 * 静态页面
 * @param {String}	staticPath	路径
 */
export async function server<Ext extends ExtendedObject = {user: object}>(
	this: serverThis<Ext> | undefined,
	staticPath: string | string[],
	opt: Opt,
): Promise<Response | undefined> {
	//路径应为文件夹的数组
	let match = this && this.match || pathMatch;
	let accept = opt.getHeader('accept') || '';
	accept = (Array.isArray(accept) ? accept.join(',') : accept).split(/\s*,\s*/);
	let index = this && this.index && this.index(accept) || ['index.html'];
	let extension = this && this.index && this.extension(accept) || ['.html'];
	if (!Array.isArray(index)) { index = [index]; }
	if (!Array.isArray(extension)) { extension = [extension]; }
	const baseActive = getStaticResponse.bind({ basePath: this && this.basePath, index, extension }) as staticActive;
	const active: staticActive = typeof (this && this.active) === 'function' ? this!.active!.bind(null, baseActive) : baseActive;
	const config = this && this.config;
	const isDebug = Boolean(config && config.isDebug);

	staticPath = Array.isArray(staticPath) ?
		staticPath.map(String) :
		(typeof staticPath === 'string' ? staticPath.split(/[\\\/]+/) : []);

	//循环找到第一个路径环境
	for await (let [vpath, module, appId] of match(staticPath, {
		type: 'static',
		isDebug,
		domain: opt.domain,
		method: opt.method,
		path: opt.path,
		clientIp: opt.clientIp,
		util: config && config.util,
		api: config && config.api,
		getHeader: opt.getHeader,
		setHeader: opt.setHeader,
	})) {
		if (typeof module !== 'string') { continue; }
		let rpath: string | string[] = staticPath.slice(vpath.length);
		rpath = rpath.length ? '/' + rpath.join('/') : '';
		//获取实际路径
		const res = await active(module, rpath, appId, vpath.length > 1 ? '/' + vpath.slice(1).join('/') : '');
		if (res) { return res; }
	}
}

export default function init<Ext extends ExtendedObject = {user: object}>(
	config: Config<Ext>,
	match: pathMatch,
): server {
	return server.bind({
		config,
		match,
		get active() { return config.static.active; },
		basePath: (appId: string) => config.static.basePath(appId),
		index: (accept: string[]) => config.static.index(accept),
		extension: (accept: string[]) => config.static.extension(accept),
	} as serverThis<Ext>);
}
